import type { NotificationCenterItemDto, NotificationCenterSeverityToken } from '@invento/shared';

export type NotificationTone = 'info' | 'warning' | 'danger';
type NotificationTranslator = (key: string, options?: Record<string, unknown>) => string;

const SAFE_CONTEXT_KEYS = [
  'amount',
  'count',
  'summaryDate',
  'threshold',
] as const;

type SafeContextKey = (typeof SAFE_CONTEXT_KEYS)[number];

const SAFE_CONTEXT_KEY_SET = new Set<string>(SAFE_CONTEXT_KEYS);
const SAFE_STRING_CONTEXT_KEYS = new Set<string>(['amount', 'summaryDate']);

function safeContext(context: Record<string, unknown>): Partial<Record<SafeContextKey, string | number>> {
  const values: Partial<Record<SafeContextKey, string | number>> = {};

  for (const [key, value] of Object.entries(context)) {
    if (!SAFE_CONTEXT_KEY_SET.has(key)) continue;
    if (typeof value === 'number') {
      values[key as SafeContextKey] = value;
    }
    if (typeof value === 'string' && SAFE_STRING_CONTEXT_KEYS.has(key) && value.length <= 64) {
      values[key as SafeContextKey] = value;
    }
  }

  return values;
}

export function notificationTone(severity: NotificationCenterSeverityToken): NotificationTone {
  if (severity === 'error') return 'danger';
  if (severity === 'warning') return 'warning';
  return 'info';
}

export function notificationSeverityLabel(
  severity: NotificationCenterSeverityToken,
  t: NotificationTranslator,
): string {
  return t(`severity.${severity}`, { defaultValue: severity });
}

export function notificationPreferenceLabel(
  preferenceKey: NotificationCenterItemDto['preferenceKey'],
  t: NotificationTranslator,
): string {
  return t(`preferences.${preferenceKey}`, { defaultValue: t('preferences.unknown') });
}

export function notificationDisplayCopy(
  notification: NotificationCenterItemDto,
  t: NotificationTranslator,
): { title: string; description: string } {
  const context = safeContext(notification.context);
  const titleFallback = t('fallback.title');
  const descriptionFallback = t('fallback.description');

  return {
    title: t(`templates.${notification.templateToken}.title`, {
      defaultValue: titleFallback,
      ...context,
    }),
    description: t(`templates.${notification.templateToken}.description`, {
      defaultValue: descriptionFallback,
      ...context,
    }),
  };
}

export function notificationDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale || 'en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
